// Блок 2 «Мастер-оптик» — калькуляторы 10–12 прототипа.

import { STANDARD_BLANKS } from './constants';
import {
  decentrationPerEye,
  fromVector,
  prismByPrentice,
  sphericalEquivalent,
  toVector,
  type Rx,
} from './core';

// --- 10. Диаметр заготовки ----------------------------------------------------

export interface BlankSizeInput {
  /** A — ширина проёма оправы, мм (в прототипе ED принят равным A). */
  a: number;
  /** DBL — переносица, мм. */
  dbl: number;
  /** PD пациента, мм. */
  pd: number;
  /** Запас на обработку, мм. */
  margin: number;
}

export interface BlankOption {
  /** Диаметр из стандартного ряда, мм. */
  size: number;
  /** Хватает ли этой заготовки. */
  fits: boolean;
}

export interface BlankSizeResult {
  /** Децентрация на глаз, мм (по модулю). */
  decentration: number;
  /** Минимальный диаметр заготовки (MBS), мм. */
  mbs: number;
  /** Стандартный ряд с отметкой «подходит / не подходит». */
  blanks: BlankOption[];
}

/**
 * MBS = ED + 2·Dec + 2·запас, ED ≈ A; ряд заготовок помечается по MBS.
 *
 * PD здесь бинокулярный, поэтому в `decentrationPerEye` уходит его половина
 * (см. D6 в core.ts) — числа прототипа при этом сохраняются.
 */
export function blankSize({ a, dbl, pd, margin }: BlankSizeInput): BlankSizeResult {
  const decentration = Math.abs(decentrationPerEye(a, dbl, pd / 2));
  const mbs = a + 2 * decentration + 2 * margin;
  return {
    decentration,
    mbs,
    blanks: STANDARD_BLANKS.map((size) => ({ size, fits: size >= mbs })),
  };
}

// --- 11. Наклон и изгиб оправы ------------------------------------------------

export interface TiltWrapInput {
  rx: Rx;
  /** Пантоскопический наклон, °. */
  pantoDeg: number;
  /** Угол оборачиваемости оправы (wrap), °. */
  wrapDeg: number;
}

/**
 * Компенсация наклона и изгиба оправы (приближение по формулам Мартина).
 *
 * Наведённый наклоном цилиндр SE·tan²(угол) раскладывается в оптический вектор
 * (ось 180° для пантоскопического наклона, 90° для изгиба) и ВЫЧИТАЕТСЯ из
 * рецепта — то есть на выходе Rx, который надо заказать, чтобы на глазу
 * получился исходный. Перенесено из прототипа без изменений.
 */
export function tiltWrapCompensation({ rx, pantoDeg, wrapDeg }: TiltWrapInput): Rx {
  const se = sphericalEquivalent(rx.sphere, rx.cyl);
  const tiltCyl = se * Math.pow(Math.tan((pantoDeg * Math.PI) / 180), 2);
  const wrapCyl = se * Math.pow(Math.tan((wrapDeg * Math.PI) / 180), 2);

  const base = toVector(rx);
  const tilt = toVector({ sphere: 0, cyl: tiltCyl, axis: 180 });
  const wrap = toVector({ sphere: 0, cyl: wrapCyl, axis: 90 });

  return fromVector({
    M: base.M - tilt.M - wrap.M,
    J0: base.J0 - tilt.J0 - wrap.J0,
    J45: base.J45 - tilt.J45 - wrap.J45,
  });
}

// --- 12. Вертикальный дисбаланс -----------------------------------------------

export type ImbalanceVerdict = 'none' | 'borderline' | 'slabOff';

export interface VerticalImbalanceInput {
  /** Сфера правого глаза (OD), дптр. */
  odPower: number;
  /** Сфера левого глаза (OS), дптр. */
  osPower: number;
  /** Насколько точка чтения ниже оптического центра, мм. */
  readingDrop: number;
}

export interface VerticalImbalanceResult {
  /** Призма правого глаза в точке чтения, Δ. */
  prismOd: number;
  /** Призма левого глаза в точке чтения, Δ. */
  prismOs: number;
  /** Разница между глазами, Δ. */
  difference: number;
  verdict: ImbalanceVerdict;
}

/**
 * Вертикальный дисбаланс в точке чтения.
 *
 * Правило Прентиса применяется к каждому глазу, разница берётся ПО СИЛАМ:
 * Δ = |(F_od − F_os) · dec| / 10. Пороги вердикта — из прототипа: до 1.0 Δ
 * компенсация не нужна, до 2.0 Δ — пограничный случай, дальше — slab-off.
 *
 * ОТКЛОНЕНИЕ ОТ ПРОТОТИПА (D4). Прототип вычитал МОДУЛИ призм
 * (||F_od| − |F_os||·dec/10). Для однознаковой анизометропии это то же самое,
 * но при разных знаках (например OD −4.00 / OS +1.00) призмы направлены в
 * разные стороны и дисбаланс складывается: 5.00 Δ вместо 3.00 Δ у прототипа —
 * то есть у прототипа как раз в самом опасном случае получалось «всё хорошо».
 * Исправлено.
 */
export function verticalImbalance({
  odPower,
  osPower,
  readingDrop,
}: VerticalImbalanceInput): VerticalImbalanceResult {
  const difference = prismByPrentice(odPower - osPower, readingDrop);
  return {
    prismOd: prismByPrentice(odPower, readingDrop),
    prismOs: prismByPrentice(osPower, readingDrop),
    difference,
    verdict: difference < 1.0 ? 'none' : difference < 2.0 ? 'borderline' : 'slabOff',
  };
}
